import React from 'react';
import { useAdminShell } from './AdminShellContext';
import { Btn, EmptyLine, MoreMenu, Pill } from './ui';

/** A cell's content: plain text truncates, JSX (an avatar row, a pill) is placed as-is. */
const isText = (v) => typeof v === 'string' || typeof v === 'number';

/**
 * The one table. A white radius-24 card with 12px of padding: 9px column labels, rows
 * at min-height 64 with radius 18, 32px action pills, and a round pager. At 720–1179px
 * (tablet) and below 720px (phone) the same rows become a card grid — one component,
 * never a parallel mobile table.
 *
 * columns: [{ key, label, fr = 1, right = false, render?(row), card }]
 *   `card` places the column on the small-screen card: 'kicker' (magenta line above the
 *   title) · 'title' · 'badge' (status pill, top right) · 'pair' (a label/value tile,
 *   the default) · 'hide'. Without any `card` flags the first column is the title and
 *   the rest are pairs, which is what every existing page expects.
 *
 * actions(row): [{ label, onClick, variant }] — return [] for a row with nothing to do.
 * hasActions: whether the person holds the table's write verb. True ⇒ the trailing
 *   column is 150px and a card gets its ⋯ menu; false ⇒ 40px and no menu. Absent, never
 *   disabled. On a card the actions live behind ONE ⋯ button at the top right (`MoreMenu`)
 *   — a foot of six pills under every row made the phone view a wall of buttons.
 * actionsMenu: put the desktop row's actions behind the same ⋯ (Oct 28 2026, USERS and HOSTS),
 *   with each action's `icon`. The trailing column shrinks to the button.
 */
export default function DataTable({
  columns,
  rows,
  rowKey = (r) => r.id,
  actions,
  hasActions = false,
  actionsMenu = false,
  onRowClick,
  selected,
  rowImage,
  imageRadius = 14,
  empty = 'NOTHING HERE YET.',
  title,
  right,
  total,
  page,
  pageSize,
  onPage,
  footerLeft,
  loadMore,
  loading = false,
  expanded,
  className = '',
}) {
  const { isMobile, isTablet } = useAdminShell();
  const cards = isMobile || isTablet;
  const cols = columns.filter(Boolean);
  const gridCols = `${cols.map((c) => `${c.fr ?? 1}fr`).join(' ')} ${actions ? (hasActions ? (actionsMenu ? '56px' : 'minmax(150px, max-content)') : '40px') : ''}`.trim();
  const totalPages = total != null && pageSize ? Math.max(1, Math.ceil(total / pageSize)) : null;
  const rowBg = (row) => (selected?.(row) ? '#FFF0F8' : 'transparent');

  const head = (title || right) && (
    <div className="flex flex-wrap items-baseline justify-between gap-[10px] px-2 py-[6px]">
      {title && <span className="font-mono text-[12px] font-bold uppercase tracking-[0.16em]">{title}</span>}
      {right && <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#9CA3AF]">{right}</span>}
    </div>
  );

  const footer = (total != null || footerLeft || loadMore) && (
    <div className="flex flex-wrap items-center justify-between gap-[10px] px-2 pb-1 pt-[10px]">
      <span className="font-mono text-[11px] text-[#9CA3AF]">{footerLeft ?? `${(total ?? 0).toLocaleString()} TOTAL`}</span>
      {loadMore ? (
        <Btn size="card" onClick={loadMore}>LOAD 100 MORE</Btn>
      ) : totalPages != null && onPage ? (
        <div className="flex items-center gap-[6px]">
          <button type="button" aria-label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)} className="h-9 w-9 rounded-full bg-[#F2F2F2] font-mono text-[13px] disabled:opacity-40">←</button>
          <span className="flex h-9 items-center rounded-full bg-[#F7F7F7] px-[14px] font-mono text-[10px] font-bold uppercase tracking-[0.1em]">PAGE {page} / {totalPages.toLocaleString()}</span>
          <button type="button" aria-label="Next page" disabled={page >= totalPages} onClick={() => onPage(page + 1)} className="h-9 w-9 rounded-full bg-black font-mono text-[13px] text-white disabled:opacity-40">→</button>
        </div>
      ) : null}
    </div>
  );

  const loadingBox = <div className="rounded-[20px] bg-[#F7F7F7] px-4 py-10 text-center font-mono text-[12px] font-bold uppercase tracking-[0.14em] text-[#6B7280]">LOADING…</div>;

  if (cards) {
    // Which column plays which part on a card. No flags ⇒ first is the title, rest pairs.
    const flagged = cols.some((c) => c.card);
    const roleOf = (c, i) => c.card || (flagged ? 'pair' : i === 0 ? 'title' : 'pair');
    const titleCol = cols.find((c, i) => roleOf(c, i) === 'title') || cols[0];
    const kickerCol = cols.find((c, i) => roleOf(c, i) === 'kicker');
    const badgeCol = cols.find((c, i) => roleOf(c, i) === 'badge');
    const pairCols = cols.filter((c, i) => roleOf(c, i) === 'pair' && c.label);
    const val = (c, row) => (c.render ? c.render(row) : (row[c.key] ?? '—'));
    return (
      <div className={`flex min-w-0 flex-col gap-[6px] rounded-[24px] bg-white ${isMobile ? 'p-2' : 'p-3'} ${className}`}>
        {head}
        <div className="grid gap-2" style={{ gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'repeat(auto-fill, minmax(300px, 1fr))' }}>
          {rows.map((row) => {
            const acts = hasActions && actions ? (actions(row) || []) : [];
            const img = rowImage?.(row);
            return (
              <article
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`flex min-w-0 flex-col rounded-[20px] ${isMobile ? 'gap-2 px-3 py-[10px]' : 'gap-3 px-3 py-[14px]'} ${onRowClick ? 'cursor-pointer' : ''}`}
                style={{ background: rowBg(row), boxShadow: 'inset 0 0 0 2px #F2F2F2' }}
              >
                <div className="flex items-start gap-3">
                  {img !== undefined && img !== null && (
                    <span className="h-12 w-12 flex-shrink-0 overflow-hidden bg-[#F2F2F2]" style={{ borderRadius: imageRadius }}>
                      {img ? <img src={img} alt="" className="h-full w-full object-cover" /> : null}
                    </span>
                  )}
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    {kickerCol && <span className="font-mono text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#FF00AA]">{val(kickerCol, row)}</span>}
                    <span className="min-w-0 break-words font-mono text-[14px] font-bold leading-[1.35]">{val(titleCol, row)}</span>
                  </div>
                  {badgeCol && <span className="flex-shrink-0">{val(badgeCol, row)}</span>}
                  {acts.length > 0 && <MoreMenu items={acts} label="Row actions" testId="row-actions" />}
                </div>
                {pairCols.length > 0 && (
                  <div className="grid grid-cols-2 gap-1">
                    {pairCols.map((c) => (
                      <div key={c.key} className={`flex min-w-0 flex-col rounded-[14px] bg-[#F7F7F7] ${isMobile ? 'gap-[2px] px-[10px] py-[7px]' : 'gap-1 px-3 py-[10px]'}`}>
                        <span className="font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-[#9CA3AF]">{c.label}</span>
                        <span className="min-w-0 truncate font-mono text-[12px] font-bold">{val(c, row)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {expanded?.(row)}
              </article>
            );
          })}
          {loading && rows.length === 0 && loadingBox}
          {!loading && rows.length === 0 && <EmptyLine>{empty}</EmptyLine>}
        </div>
        {footer}
      </div>
    );
  }

  return (
    <div className={`flex min-w-0 flex-col gap-[6px] rounded-[24px] bg-white p-3 ${className}`}>
      {head}
      <div className="overflow-x-auto">
        <div style={{ minWidth: Math.max(640, cols.length * 110 + (actions ? (hasActions && !actionsMenu ? 220 : 56) : 0)) }}>
          <div className="grid px-2" style={{ gridTemplateColumns: gridCols }}>
            {cols.map((c) => (
              <div key={c.key} className="whitespace-nowrap px-[10px] py-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-[#9CA3AF]" style={{ textAlign: c.right ? 'right' : 'left' }}>{c.label}</div>
            ))}
            {actions && <div />}
          </div>
          {rows.map((row) => {
            const acts = hasActions && actions ? (actions(row) || []) : [];
            const ex = expanded?.(row);
            return (
              <React.Fragment key={rowKey(row)}>
                <div
                  className={`grid items-center rounded-[18px] px-2 transition-colors duration-200 hover:bg-[#F7F7F7] ${onRowClick ? 'cursor-pointer' : ''}`}
                  style={{ gridTemplateColumns: gridCols, minHeight: 64, background: rowBg(row) }}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {cols.map((c, ci) => {
                    const v = c.render ? c.render(row) : (row[c.key] ?? '—');
                    return (
                      <div key={c.key} className={`flex min-w-0 items-center gap-3 p-[10px] font-mono ${ci === 0 ? 'text-[13px] font-bold' : 'text-[12px]'} ${c.right ? 'justify-end' : ''}`}>
                        {isText(v) ? <span className="min-w-0 max-w-full truncate">{v}</span> : v}
                      </div>
                    );
                  })}
                  {actions && (
                    <div className="flex flex-wrap justify-end gap-[6px] px-1 py-2" onClick={(e) => e.stopPropagation()}>
                      {actionsMenu
                        ? <MoreMenu items={acts} label="Row actions" size={36} testId="row-actions" />
                        : acts.map((a) => (a.divider ? null : <Btn key={a.label} size="row" variant={a.variant || 'plain'} onClick={a.onClick}>{a.label}</Btn>))}
                    </div>
                  )}
                </div>
                {ex && <div className="mx-2 mb-2 rounded-[18px] bg-[#F7F7F7] px-[14px] py-3">{ex}</div>}
              </React.Fragment>
            );
          })}
          {loading && rows.length === 0 && <div className="m-2">{loadingBox}</div>}
          {!loading && rows.length === 0 && <div className="m-2"><EmptyLine>{empty}</EmptyLine></div>}
        </div>
      </div>
      {footer}
    </div>
  );
}

/** Re-exported so a page can give a cell a status pill without a second import. */
export { Pill };
